import PropTypes from "prop-types";

/**
 * Retorna o status de uma conta a pagar.
 * Na lógica de comparação foi necessário adicionar o timezone T00:00 e converter para a mesma hora
 * para evitar que se a conta estiver a vencer no próprio dia não dê falso atraso em razão da diferença
 * das horas.
 *
 * @param {Object} subgroup - O item da conta a pagar.
 * @param {number} subgroup.amountPayed - O valor pago da conta.
 * @param {string} subgroup.dueDate - A data de vencimento da conta.
 * @returns {string} - O status da conta ('Pago', 'Atrasado' ou 'Pendente').
 */
export const getBillingPayableStatus = (item) => {
  if (item.amountPayed) return 'Pago';

  const dueDate = new Date(`${item.dueDate}T00:00`).setHours(0, 0, 0, 0);
  const today = new Date().setHours(0, 0, 0, 0);
  const isOverdue = today > dueDate;
  return isOverdue ? 'Atrasado' : 'Pendente';
};

/**
 * Converte uma string de snake_case para camelCase.
 *
 * @param {string} str - A string em snake_case.
 * @returns {string} - A string convertida para camelCase.
 * // Exemplo de uso:
 * const str = 'nome_completo';
 * const result = toCamelCase(str);
 * // result => 'nomeCompleto'
 */
const toCamelCase = (str) => str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());

/**
 * Converte as chaves de um array de objetos de snake_case para camelCase.
 *
 * @param {Array<Object>} arr - O array de objetos com chaves em snake_case.
 * @returns {Array<Object>} - O array de objetos com chaves convertidas para camelCase.
 *
 * @example
 * // Exemplo de uso:
 * const data = [{ id: 1, nome_completo: 'Fulano de Tal' }];
 * const result = convertKeysToCamelCase(data);
 * // result => [{ id: 1, nomeCompleto: 'Fulano de Tal' }]
 */
export const convertKeysToCamelCase = (input) => {
  if (Array.isArray(input)) {
    return input.map(convertKeysToCamelCase);
  }

  if (input !== null && typeof input === 'object') {
    return Object.entries(input).reduce((acc, [key, value]) => {
      const newKey = toCamelCase(key);
      acc[newKey] = convertKeysToCamelCase(value);
      return acc;
    }, {});
  }

  return input; // fallback for primitives
};

/**
 * Retorna a ação de um item.
 * Através da ação chama-se os métodos dataCreate, dataEdit, dataDelete da api.
 *
 * @typedef {Object} ACTIONS
 * @property {string} ADD - Represents the 'add' action.
 * @property {string} EDIT - Represents the 'edit' action.
 * @property {string} DELETE - Represents the 'delete' action.
 */
export const ACTIONS = {
  ADD: 'add',
  DELETE: 'delete',
  EDIT: 'edit',
  UPLOAD: 'upload',
  VIEW: 'view',
};

/**
 * Tipos de colunas para usar em operações de comparação, filtro e formatação.
 *
 * @typedef {Object} COLUMN_TYPE
 * @property {string} BUTTONS - Tipo de coluna para botões.
 * @property {string} DATE - Tipo de coluna para datas.
 * @property {string} MONEY - Tipo de coluna para valores monetários.
 * @property {string} NUMBER - Tipo de coluna para valores numéricos gerais.
 * @property {string} STRING - Tipo de coluna para strings.
 */
export const COLUMN_TYPE = {
  BOOLEAN: 'boolean',
  BUTTONS: 'buttons',
  DATE: 'date',
  MONEY: 'money',
  NUMBER: 'number',
  PERCENTAGE: 'percentage',
  STRING: 'string',
};

/**
 * Formata uma string de data para o padrão pt-BR.
 *
 * @param {string} dateString - A string da data a ser formatada.
 * @returns {string} - A data formatada no padrão pt-BR (dd/mm/yyyy).
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  date.setMinutes(date.getMinutes() + date.getTimezoneOffset()); // Adjust for timezone
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const formatNumber = (number) => {
  if (typeof number === 'number' && !Number.isNaN(number)) {
    const numDigits = (number % 1 !== 0) ? 2 : 0;
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: numDigits, maximumFractionDigits: numDigits }).format(number);
  }

  if (typeof number === 'string' && !Number.isNaN(Number(number))) {
    const numericValue = parseFloat(number);
    const hasDecimals = numericValue % 1 !== 0;
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: hasDecimals ? 2 : 0 }).format(numericValue);
  }

  return number;
};

export const formatMoneyValue = (value) => (
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
)

/**
 * Formata o valor de uma célula da tabela EnhancedTable conforme seu tipo.
 *
 * @param {Object} item - O item da célula.
 * @param {Object} column - A coluna da célula.
 * @param {string} column.field - O campo da coluna.
 * @param {string} column.type - O tipo da coluna ('date', 'money', etc.).
 * @returns {string|number} - O valor formatado da célula.
 */
export const formatCellValue = (item, column) => {
  const value = item[column.field];

  switch (column.type) {
    case COLUMN_TYPE.BOOLEAN:
      return value ? 'Sim' : 'Não';

    case COLUMN_TYPE.DATE:
      return formatDate(value);

    case COLUMN_TYPE.MONEY:
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

    case COLUMN_TYPE.NUMBER:
      return formatNumber(value);

    case COLUMN_TYPE.PERCENTAGE:
      return value ? `${formatNumber(value)}%` : '';

    default:
      return value;
  }
};

/**
 * Formata valores de entrada, substituindo vírgulas por pontos em números decimais.
 *
 * @param {string|number} value - O valor a ser formatado.
 * @returns {string|number} - O valor formatado.
 *
 * Exemplos:
 *  formatValue("0,01")        // "0.01"
 *  formatValue("0,1")         // "0.1"
 *  formatValue("10,99")       // "10.99"
 *  formatValue("12341234")    // "12341234"
 *  formatValue("texto")       // "texto"
 *  formatValue("2025-01-01")  // "2025-01-01"
 *  formatValue(123)           // 123
 *  formatValue(null)          // null
 */
const valueIsInMoneyFormat = (value) => typeof value === 'string' && /^-?\d+(,\d+)?$/.test(value);

/** Tratamento e conversão de valores digitados usando a máscara correta para o formato padrão para armazenamento em banco.
 *
 * @param {string|number} value - O valor a ser formatado.
 * @returns {string|number} - O valor formatado.
 *
 */
export const formatInputValuesToDefault = (value) => {
  if (valueIsInMoneyFormat(value)) return value.replace(',', '.');
  return value;
};

/**
 * Valores padrão para uma conta a pagar (Contas a Pagar).
 *
 * @typedef {Object} CONTAS_A_PAGAR_VALUES_DEFAULT
 * @property {number|undefined} id - O identificador único da conta a pagar.
 * @property {string} name - O nome da conta/despesa.
 * @property {number} accountGroupId - ID do grupo contábil relacionado à conta.
 * @property {number} accountSubgroupId - ID do subgrupo contábil relacionado à conta.
 * @property {string} amount - O valor total da conta a pagar.
 * @property {string} dueDate - A data de vencimento da conta (formato ISO ou string).
 * @property {string} amountPayed - O valor já pago referente à conta.
 * @property {string} paymentDate - A data em que o pagamento foi realizado.
 * @property {number} suppliersId - ID do fornecedor relacionado à conta.
 * @property {Array} billingInstallments - Lista de parcelas associadas à conta.
 */
export const CONTAS_A_PAGAR_VALUES_DEFAULT = {
  id: undefined,
  name: '',
  accountGroupId: 0,
  accountSubgroupId: 0,
  amount: '',
  dueDate: '',
  amountPayed: '',
  paymentDate: '',
  suppliersId: 0,
  billingInstallments: [],
};

/**
 * Valores padrão para uma conta a receber (Contas a Receber).
 *
 * @typedef {Object} CONTAS_A_RECEBER_VALUES_DEFAULT
 * @property {number|undefined} id - O identificador único da conta a receber.
 * @property {string} name - O nome associado à conta.
 * @property {number} accountGroupId - ID do grupo contábil relacionado à conta.
 * @property {number} accountSubgroupId - ID do subgrupo contábil relacionado à conta.
 * @property {string} amount - O valor a ser recebido.
 * @property {string} dueDate - A data de vencimento do recebimento.
 * @property {string} payer - O nome do pagador.
 * @property {string} cpfCnpj - O CPF ou CNPJ do pagador.
 * @property {string} amountPayed - O valor já recebido.
 * @property {string} receiptDate - A data em que o pagamento foi recebido.
 * @property {Date|null} createdAt - A data de criação da conta.
 * @property {Date|null} updatedAt - A data da última atualização da conta.
 * @property {Array} billingInstallments - Lista de parcelas associadas à conta.
 */
export const CONTAS_A_RECEBER_VALUES_DEFAULT = {
  id: undefined,
  name: '',
  accountGroupId: 0,
  accountSubgroupId: 0,
  amount: '',
  dueDate: '',
  payer: '',
  cpfCnpj: '',
  amountPayed: '',
  receiptDate: '',
  createdAt: null,
  updatedAt: null,
  billingInstallments: [],
};

/**
 * Default values for a bank account (Conta Corrente).
 * Valores padrão para uma conta corrente.
 *
 * @typedef {Object} CONTA_CORRENTE_VALUES_DEFAULT
 * @property {number | undefined} id - The unique identifier for the account, initially undefined.
 * @property {string} banco - The name of the bank, default is "Banco do Brasil".
 * @property {number | undefined} bankCode - The bank code, default is "001".
 * @property {string} agencia - The agency number, default is "1111-5".
 * @property {string} conta - The account number, default is "67890-1".
 * @property {string} descricao - A description for the account, default is "Conta BB Principal".
 */
export const CONTA_CORRENTE_VALUES_DEFAULT = {
  id: undefined,
  bankCode: undefined,
  banco: '',
  agencia: '',
  conta: '',
  descricao: '',
};

/**
 * Default values for an OFX file.
 * Valores padrão para um arquivo OFX.
 *
 * @typedef {Object} ARQUIVO_OFX_VALUES_DEFAULT
 * @property {string} routingNumber - The bank routing number.
 * @property {string} accountNumber - The bank account number.
 * @property {string} balanceDate - The balance date.
 * @property {string} interval - The date interval.
 * @property {string} transactionsQty - The number of transactions.
 * @property {string} fileName - The name of the file.
 */
export const ARQUIVO_OFX_VALUES_DEFAULT = {
  routingNumber: '',
  accountNumber: '',
  balanceDate: '',
  interval: '',
  transactionsQty: '',
  fileName: '',
  transactions: [],
};

/**
 * @typedef {Object} TIPO_IMPOSTO_VALUES_DEFAULT
 * @property {number} id - The unique identifier of the tax.
 * @property {string} code - The code of the tax (up to 50 characters).
 * @property {string} name - The name of the tax (up to 100 characters).
 * @property {'Federal'|'Estadual'|'Municipal'} type - The type of the tax.
 * @property {number|null} standardRate - The standard rate of the tax (decimal with 2 digits).
 * @property {string|null} calculationBase - The base for tax calculation.
 * @property {string|null} createdAt - Timestamp of creation (ISO 8601 format).
 * @property {string|null} updatedAt - Timestamp of last update (ISO 8601 format).
 * @property {number|null} accountGroupId - ID of the associated account group.
 * @property {number|null} accountSubgroupId - ID of the associated account subgroup.
 * @property {Object|null} accountGroup - Associated account group object.
 * @property {Object|null} accountSubgroup - Associated account subgroup object.
 */
export const TIPO_IMPOSTO_VALUES_DEFAULT = {
  id: 0,
  code: '',
  name: '',
  type: null,
  standardRate: null,
  calculationBase: null,
  createdAt: null,
  updatedAt: null,
  accountGroupId: null,
  accountSubgroupId: null,
  accountGroup: null,
  accountSubgroup: null,
};

/**
 * @typedef {Object} FORNECEDOR_VALUES_DEFAULT
 * @property {number} id - Chave primária para identificação única.
 * @property {string} companyName - Nome do fornecedor, essencial para identificação.
 * @property {string} tradeName - Nome fantasia do fornecedor.
 * @property {string} cpfCnpj - Permite armazenar tanto o CNPJ quanto o CPF, dependendo do tipo de fornecedor.
 * @property {string} entityType - Indica se o fornecedor é pessoa física ou jurídica.
 * @property {string} stateRegistration - Necessário para empresas que possuem essa inscrição.
 * @property {string} phone - Facilita o contato com o fornecedor.
 * @property {string} email - Facilita o contato com o fornecedor.
 * @property {string} address - Detalhes do endereço.
 * @property {string} city - Detalhes do endereço.
 * @property {string} stateCode - Detalhes do endereço.
 * @property {string} postalCode - Detalhes do endereço.
 * @property {string} contactPerson - Nome do responsável pelo contato.
 * @property {string} providedServices - Descreve os serviços oferecidos pelo fornecedor (ex.: energia elétrica, telefonia).
 * @property {boolean} isActive - Permite controlar fornecedores que não estão mais ativos.
 * @property {string} notes - Campo flexível para informações extras.
 * @property {Date|null} createdAt - Ajuda a acompanhar o histórico de cadastros.
 * @property {Date|null} updatedAt - Ajuda a acompanhar o histórico de cadastros.
 */
export const FORNECEDOR_VALUES_DEFAULT = {
  id: 0,
  companyName: '',
  tradeName: '',
  cpfCnpj: '',
  entityType: 'Pessoa Jurídica',
  stateRegistration: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  stateCode: '',
  postalCode: '',
  contactPerson: '',
  providedServices: '',
  isActive: 1,
  notes: '',
  createdAt: null,
  updatedAt: null,
};

/**
 * Valores padrão para um objeto de funcionário.
 *
 * @typedef {Object} EMPLOYEE_VALUES_DEFAULT
 * @property {number|undefined} id - O identificador único do funcionário.
 * @property {string} fullName - O nome completo do funcionário.
 * @property {string} birthDate - A data de nascimento do funcionário.
 * @property {string} cpf - O número do CPF (Cadastro de Pessoas Físicas) do funcionário.
 * @property {string} address - O endereço do funcionário.
 * @property {string} city - A cidade do endereço do funcionário.
 * @property {string} stateCode - O estado do endereço do funcionário.
 * @property {string} postalCode - O CEP do endereço do funcionário.
 * @property {string} phone - O número de telefone do funcionário.
 * @property {string} email - O endereço de e-mail do funcionário.
 * @property {string} admissionDate - A data de admissão do funcionário.
 * @property {string} position - O cargo do funcionário.
 * @property {string} department - O departamento do funcionário.
 * @property {string} salary - O salário do funcionário.
 * @property {string} employmentStatus - O status de emprego do funcionário. O padrão é 'Ativo'.
 * @property {string} observations - Qualquer observação sobre o funcionário.
 * @property {Date|null} createdAt - A data de criação do registro do funcionário.
 * @property {Date|null} updatedAt - A data da última atualização do registro do funcionário.
 */
export const EMPLOYEE_VALUES_DEFAULT = {
  id: undefined,
  fullName: '',
  birthDate: '',
  cpf: '',
  address: '',
  city: '',
  stateCode: 'MG',
  postalCode: '',
  phone: '',
  email: '',
  admissionDate: '',
  position: '',
  department: '',
  salary: '',
  employmentStatus: 'Ativo',
  observations: '',
  createdAt: null,
  updatedAt: null,
};


/**
 * An array of Brazilian state abbreviations.
 * @typedef {Object} ESTADOS_DO_BRASIL
 */
export const ESTADOS_DO_BRASIL = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];


/**
 * Types of account group
 * @typedef {Object} ACCOUNT_GROUP_TYPE
 * @property {string} INCOME - Receitas.
 * @property {string} OUTCOME - Despesas.
 */
export const ACCOUNT_GROUP_TYPE = {
  INCOME: 'INCOME',
  OUTCOME: 'OUTCOME',
  RESULT: 'RESULT',
}


/**
 * Billing installment data structure
 * @typedef {Object} BillingInstallment
 * @property {number} [id] - ID da parcela.
 * @property {number} [billingPayableId] - ID da conta a pagar relacionada (opcional).
 * @property {number} [billingReceivableId] - ID da conta a receber relacionada (opcional).
 * @property {number} installmentNumber - Número da parcela (ex: 1, 2, 3...).
 * @property {string} dueDate - Data de vencimento da parcela.
 * @property {number} amount - Valor da parcela.
 * @property {number} [amountPayed] - Valor pago até o momento (opcional).
 * @property {string} [paymentDate] - Data de pagamento da parcela, se já paga.
 */
export const BillingInstallmentPropTypes = {
  id: PropTypes.number,
  billingPayableId: PropTypes.number,
  billingReceivableId: PropTypes.number,
  installmentNumber: PropTypes.number.isRequired,
  dueDate: PropTypes.string.isRequired,
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  amountPayed: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  paymentDate: PropTypes.string,
};


/**
 * Estrutura de dados para um grupo contábil (Account Group)
 * @typedef {Object} AccountGroup
 * @property {number} id - ID único do grupo contábil.
 * @property {string} name - Nome do grupo contábil.
 * @property {string} classification - Código de classificação do grupo (ex: "7.2").
 * @property {string} type - Tipo do grupo ("INCOME" ou "OUTCOME").
 * @property {string} createdAt - Data de criação do grupo (formato ISO).
 * @property {string} updatedAt - Data da última atualização do grupo (formato ISO).
 */
export const AccountGroupPropTypes = {
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  classification: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
  updatedAt: PropTypes.string.isRequired,
};


/**
 * Estrutura de dados para um subgrupo contábil (Account Subgroup)
 * @typedef {Object} AccountSubgroup
 * @property {number} id - ID único do subgrupo contábil.
 * @property {string} name - Nome do subgrupo contábil.
 * @property {string} classification - Código de classificação do subgrupo (ex: "4.1.005").
 * @property {number} accountGroupId - ID do grupo contábil relacionado.
 * @property {string} createdAt - Data de criação do subgrupo (formato ISO).
 * @property {string} updatedAt - Data da última atualização do subgrupo (formato ISO).
 */
export const AccountSubgroupPropTypes = {
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  classification: PropTypes.string.isRequired,
  accountGroupId: PropTypes.number.isRequired,
  createdAt: PropTypes.string.isRequired,
  updatedAt: PropTypes.string.isRequired,
};